import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, FormGroup, Input, Label } from "reactstrap";
import SpinnerInButton from "../utils/SpinnerInButton";

interface Props {
  updateUnitCalculation: (value: number) => void;
  setFees: (fees: {foundationFee: number; annualFee: number; admissionFee: number; memberRegistrationFee: number;}) => void;
  inProgress: boolean;
  basicChargeType: string;
  stateSelection: string;
  registrationFee: string;
  employeesNumber: string;
  eventsNumber: string;
  unitCalculation: number;
  foundationFee: number;
  annualFee: number;
  admissionFee: number;
  membershipCategory: string;
  representativeName: string;
  handleChange: (event: any) => void;
  handlePrevious: (event: any) => void;
  handleSubmit: (event: any) => void;
}

// MembershipFeeCalculationSelectionForm
export const StepSix: React.FC<Props> = ({ updateUnitCalculation, setFees, inProgress, basicChargeType, stateSelection, registrationFee, employeesNumber, eventsNumber, foundationFee, unitCalculation, annualFee, admissionFee, membershipCategory, representativeName, handleChange, handlePrevious, handleSubmit}) => {

  const [isChecked, setIsChecked] = useState(false);
  const [valid, setValid] = useState(false);
  
  const handleCheckboxChange = (e: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
    setIsChecked(e.target.checked);
  };

  useEffect(() => {
    const valid = (() => {
      return !!representativeName && !!basicChargeType && !!employeesNumber && !!eventsNumber;
    })();
    setValid(valid);
    // eslint-disable-next-line
  }, [
    representativeName,
    basicChargeType,
    employeesNumber,
    eventsNumber
  ]);

  // 1. 入会金
  var memberRegistrationFee = 0;
  if (membershipCategory === "CORPORATE MEMBER/法人会員") {
    if (registrationFee === "東証・大証等１部上場又は準ずる社  (RM500)") {
      memberRegistrationFee = 500;
    }
    if ((registrationFee === "東証・大証等２部上場又は準ずる社 (RM300)") ||
    (registrationFee === "個人会員 (RM300)") ||
    (registrationFee === "在外会員(RM300)") ||
    (registrationFee === "準会員(RM300)")) {
      memberRegistrationFee = 300;
    }
    if ((registrationFee === "その他 (RM100)") || (registrationFee === "中小企業会員 (RM100)")) {
      memberRegistrationFee = 100;
    }
  } else {
    memberRegistrationFee = 300;
  }

  // 2. Company Unit
  const companyUnit = useMemo(() => {
    if (membershipCategory !== "CORPORATE MEMBER/法人会員") return 0;

    switch (basicChargeType) {
      case "A": return 15;
      case "A'": return 8;
      case "B": return 9;
      case "B'": return 5;
      case "C": return 5;
      case "C'": return 3;
      case "D": return 3;
      case "D'": return 1;
      default: return 0;
    }
  }, [membershipCategory, basicChargeType]);

  // 3. Employee Unit
  const employeeUnit = useMemo(() => {
    if (membershipCategory !== "CORPORATE MEMBER/法人会員") return 0;

    switch (employeesNumber) {
      case "1-2名": return 1;
      case "3-5名": return 2;
      case "6-11名": return 3;
      case "12-20名": return 4;
      case "21-50名": return 5;
      case "51名以上": return 6;
      default: return 0;
    }
  }, [membershipCategory, employeesNumber]);

  // 4. Event Unit
  const eventUnit = useMemo(() => {
    if (membershipCategory !== "CORPORATE MEMBER/法人会員") return 0;

    switch (eventsNumber) {
      case "3部会まで": return 1;
      case "4-5部会": return 2;
      case "6部会以上": return 3;
      default: return 0;
    }
  }, [membershipCategory, eventsNumber]);

  // 5. Non-Corporate Unit
  const noneCorporateMemberUnit = useMemo(() => {
    if (membershipCategory === "INDIVIDUAL MEMBER/個人会員" || membershipCategory === "OVERSEAS MEMBER/在外会員") {
      return 4;
    }
    if (membershipCategory === "SUB MEMBER/準会員") {
      return 3;
    }
    return 0;
  }, [membershipCategory]);

  useEffect(() => {

    const foundation =
    (membershipCategory === 'CORPORATE MEMBER/法人会員') ||
    (membershipCategory === 'INDIVIDUAL MEMBER/個人会員')
      ? 300
      : 0;

    if (stateSelection === "others") {
      const annual = unitCalculation * 360;
      const admission = annual + memberRegistrationFee + foundation;

      setFees({
        foundationFee: foundation,
        annualFee: annual,
        admissionFee: admission,
        memberRegistrationFee: memberRegistrationFee
      });
    } else {
      const annual = unitCalculation * 345;
      const admission = annual + memberRegistrationFee + foundation;

      setFees({
        foundationFee: foundation,
        annualFee: annual,
        admissionFee: admission,
        memberRegistrationFee: memberRegistrationFee
      });
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitCalculation, memberRegistrationFee, membershipCategory]);

  useEffect(() => {
    const totalUnits = Number(companyUnit) + Number(employeeUnit) + Number(eventUnit) + Number(noneCorporateMemberUnit);

    // Get current value from props
    if (totalUnits !== unitCalculation) {
      updateUnitCalculation(totalUnits);
    }
    
  }, [companyUnit, employeeUnit, eventUnit, noneCorporateMemberUnit, unitCalculation, updateUnitCalculation]);

  return (
    <div className='pt-5'>
      <h2 className='pt-5 pb-3 font-weight-bold'><strong>入会費・年会費会費の合計 / Total of Admission Fee & Annual Membership Fee</strong></h2>
      {(membershipCategory === "CORPORATE MEMBER/法人会員") && (
        <Alert color='primary' fade={false}>
          <strong>法人会員 (Corporate Member)</strong><br />本会の定款に基づき、法人会員の会員基準は実質的な最終親会社により判断いたします。そのため、法人会員の方は親会社の情報をご入力ください。<br />
          <hr />
          <p>
            <strong>親会社の登録について（補足）</strong><br/>
            日本本社が以下のいずれかに該当する場合は、日本本社を親会社としてご登録ください。<br />
            1.意思決定権を有している場合<br />
            （財務および営業、または事業を決定する機関を支配している）<br /><br />
            
            2.最終親会社である場合<br />
            判定の目安（例）<br />
            ・50％以上の株式を保有している<br />
            ・取締役、役員を過半数以上派遣している<br />
            ・資金の大半を融資している<br />
            ・連結財務諸表に含まれている<br /><br />
            ※最終的には理事会にて、日本の親会社の資本金・事業規模・売上等を総合的に判断し、決定いたします。<br /><br />
            
            年会費<br />
            ・法人会員：各費目ごとの 口数（Unit）数の合計に基づき算出しご請求いたします。<br />
            ・個人会員・在外会員・準会員：定められた口数により算出します。<br />
            ・年度途中でのご入会は 月割りで計算 いたします。<br />
            ・年会費は 上半期（1月〜6月）、下半期（7月〜12月） の2回に分けてご請求します。<br /><br />
            
            入会金・その他費用<br />
            ・入会時に入会金（会員資格に応じて初回のみ）を頂戴いたします。<br />
            ・在外会員・準会員を除き、JACTIMFOUNDATION費RM300/年をご負担いただきます。<br /><br />

            ご返金について<br />
            ・年会費およびJACTIMFOUNDATION費は、年度途中で退会される場合であっても返金の対象外となります。<br />
            なお、未納金がある場合は、年会費およびJACTIMFOUNDATION費の全額のお支払い完了後に退会の承認を行いますので、あらかじめご了承ください。<br /><br />
            
            再入会について<br />
            ・ご退会から 半年以内に再入会される場合は、入会金を免除 いたします。
          </p>
        </Alert>
      )}

      {(membershipCategory === "INDIVIDUAL MEMBER/個人会員") && (
        <Alert color='primary' fade={false}>
          <strong>個人会員 (Individual Member)</strong><br />
          <hr />
          <p>
            (※一律で年会費が表示されますが、年度途中でご入会の方については会費を月割りで計算の上、ご請求いたします。)<br/><br/>
            会費の請求については、年会費を半分に分け、上半期(1月~6月)、下半期(7月~12月)の二回に分けてご請求させていただきます。<br/><br/>
            年会費とは別に、ご入会時に入会金(ご入会時一回のみ、親会社の規模による)、在外、準会員を除く方にJACTIMFOUNDATION費300RM(一年に一回のご請求)のお支払いをお願いしております。<br/><br/>
            年会費およびJACTIMFOUNDATION費は、年度途中で退会される場合であっても返金の対象外となります。なお、未納金がある場合は、年会費およびJACTIMFOUNDATION費の全額のお支払い完了後に退会の承認を行いますので、あらかじめご了承ください。<br/><br/>
            また、ご退会後半年以内の再入会の場合は、ご入会金は免除されます。
          </p>
        </Alert>
      )}

      {(membershipCategory === "SUB MEMBER/準会員") && (
        <Alert color='primary' fade={false}>
          <strong>準会員 (Sub Member)</strong><br />
          <hr />
          <p>
            (※一律で年会費が表示されますが、年度途中でご入会の方については会費を月割りで計算の上、ご請求いたします。)<br/><br/>
            会費の請求については、年会費を半分に分け、上半期(1月~6月)、下半期(7月~12月)の二回に分けてご請求させていただきます。<br/><br/>
            年会費とは別に、ご入会時に入会金(ご入会時一回のみ、親会社の規模による)、在外、準会員を除く方にJACTIMFOUNDATION費300RM(一年に一回のご請求)のお支払いをお願いしております。<br/><br/>
            JACTIM FOUNDATION費についても年度途中でご入会の方については月割りで計算の上、ご請求いたします。) 年会費についてはご退会時にご返金させていただいておりますが、JACTIMFOUNDATION費についてはご返金の対象外となりますため、予めご承知おきくださいますようお願い申し上げます。<br/><br/>
            また、ご退会後半年以内の再入会の場合は、ご入会金は免除されます。
          </p>
        </Alert>
      )}

      {(membershipCategory === "OVERSEAS MEMBER/在外会員") && (
        <Alert color='primary' fade={false}>
          <strong>在外会員 (Overseas Member)</strong><br />
          <hr />
          <p>
            (※一律で年会費が表示されますが、年度途中でご入会の方については会費を月割りで計算の上、ご請求いたします。)<br/><br/>
            会費の請求については、年会費を半分に分け、上半期(1月~6月)、下半期(7月~12月)の二回に分けてご請求させていただきます。<br/><br/>
            年会費とは別に、ご入会時に入会金(ご入会時一回のみ、親会社の規模による)、在外、準会員を除く方にJACTIMFOUNDATION費300RM(一年に一回のご請求)のお支払いをお願いしております。<br/><br/>
            JACTIM FOUNDATION費についても年度途中でご入会の方については月割りで計算の上、ご請求いたします。) 年会費についてはご退会時にご返金させていただいておりますが、JACTIMFOUNDATION費についてはご返金の対象外となりますため、予めご承知おきくださいますようお願い申し上げます。<br/><br/>
            また、ご退会後半年以内の再入会の場合は、ご入会金は免除されます。
          </p>
        </Alert>
      )}

      <div>
        {(membershipCategory === "CORPORATE MEMBER/法人会員") && (
          <>
            <Form className='pt-5'>
              <Label>1. 入会金 / Admission Fee<span style={{ color: "red" }}> (*)</span></Label>
              <FormGroup>
                <Input
                  id="registrationFee"
                  name="registrationFee"
                  type="select"
                  value={registrationFee}
                  onChange={handleChange}
                >
                  <option></option>
                  <option key="東証・大証等１部上場又は準ずる社  (RM500)" value="東証・大証等１部上場又は準ずる社  (RM500)">東証・大証等１部上場又は準ずる社 / First Section of the Tokyo Stock Exchange or Equivalent Public Listed Companies</option>
                  <option key="東証・大証等２部上場又は準ずる社 (RM300)" value="東証・大証等２部上場又は準ずる社 (RM300)">東証・大証等２部上場又は準ずる社 / Second Section of the Tokyo Stock Exchange or Equivalent Public Listed Companies</option>
                  <option key="その他 (RM100)" value="その他 (RM100)">その他 / Others </option>
                  <option key="中小企業会員 (RM100)" value="中小企業会員 (RM100)">中小企業会員 / SME (Small and Medium Enterprise) Member</option>
                </Input>
              </FormGroup>
            </Form>

            <Form className='pt-5'>
              <p>2. 年会費 / Annual Membership Fee</p>
              <Label>a. 親会社の会社規模<span style={{ color: "red" }}> (*)</span></Label>
              <FormGroup>
                <Input
                  id="basicChargeType"
                  name="basicChargeType"
                  type="select"
                  value={basicChargeType}
                  onChange={handleChange}
                >
                  <option></option>
                  <option key="A" value="A">(A)東証・大証等１部上場又は準ずる社  (15口) / First Section of the Tokyo Stock Exchange or Equivalent Public Listed Companies (15 units)</option>
                  <option key="A'" value="A'">(A’)東証・大証等１部上場又は準ずる社の既会員のグループ企業 (8口) / Existing Member of the First Section of the Tokyo Exchange or Equivalent Group Company (8 units)</option>
                  <option key="B" value="B">(B)東証・大証等２部上場又は準ずる社 (9口) / Second Section of the Tokyo Stock Exchange or Equivalent Public Listed Companies (9 units)</option>
                  <option key="B'" value="B'">(B’)東証・大証等２部上場又は準ずる社の既会員のグループ企業 (5口) / Existing Member of the Second Section of the Tokyo Exchange or Equivalent Group Company (5 units)</option>
                  <option key="C" value="C">(C)その他 (5口) / Others (5 units)</option>
                  <option key="C'" value="C'">(C’)その他の既会員のグループ企業 (3口) / Other Existing Members of Group Company (3 units)</option>
                  <option key="D" value="D">(D)中小企業会員 (3口) / SME Member (3 units)</option>
                  <option key="D'" value="D'">(D’)中小企業会員の既会員のグループ企業 (1口) / Other Existing SME Members of Group Company (1 unit)</option>
                </Input>
              </FormGroup>
            </Form>

            <Form className='pt-5'>
              <Label>b. 【日本人の在籍数】※日本人の在籍数によって会費に付加されます。当てはまるものにチェックを入れてください。 / [Number of Japanese Employees] Additional fees may apply based on the number of Japanese employees. Please check the applicable option.<span style={{ color: "red" }}> (*)</span></Label>
              <FormGroup>
                <Input
                  id="employeesNumber"
                  name="employeesNumber"
                  type="select"
                  value={employeesNumber}
                  onChange={handleChange}
                >
                  <option></option>
                  <option key="1-2名" value="1-2名">1-2名 (1口) / 1-2 persons (1 unit)</option>
                  <option key="3-5名" value="3-5名">3-5名(2口) / 3-5 persons (2 units)</option>
                  <option key="6-11名" value="6-11名">6-11名(3口) / 6-11 persons (3 units)</option>
                  <option key="12-20名" value="12-20名">12-20名(4口) / 12-20 persons (4 units)</option>
                  <option key="21-50名" value="21-50名">21名-50名 (5口) / 21-50 persons (5 units)</option>
                  <option key="51名以上" value="51名以上">51名以上 (6口) / More than 51 persons (6 units)</option>
                </Input>
              </FormGroup>
            </Form>

            <Form className='pt-5'>
              <Label>c. 登録部会数 メイン部会(業種に基づくもの)他、2部会まで追加料金なしでご登録いただけます。 / [Parent Company's Size] Number of Registered Subcommittees - In addition to the main subcommittee (based on industry), up to two more subcommittees can be registered at no additional cost.<span style={{ color: "red" }}> (*)</span></Label>
              <FormGroup>
                <Input
                  id="eventsNumber"
                  name="eventsNumber"
                  type="select"
                  value={eventsNumber}
                  onChange={handleChange}
                >
                  <option></option>
                  <option key="3部会まで" value="3部会まで">3部会まで (1口) / Up to 3 Subcommittees (1 unit)</option>
                  <option key="4-5部会" value="4-5部会">4-5部会 (2口) / 4 to 5 Subcommittees (2 units)</option>
                  <option key="6部会以上" value="6部会以上">6部会以上 (3口) / More than 6 Subcommittees  (3 units)</option>
                </Input>
              </FormGroup>
            </Form>
          </>
        )}

        {(membershipCategory === "CORPORATE MEMBER/法人会員") && (registrationFee && basicChargeType && employeesNumber && eventsNumber) && (
          <div className="pt-5">
            <p><strong>入会費・年会費会費の合計 / Total of Admission Fee & Annual Membership Fee</strong></p>
            <div className="divider"></div>
            {(stateSelection === "others") && (
              <Badge color='success'>
                ※ 年会費は口数 X RM 360で計算します。
              </Badge>
            )}
            {(stateSelection !== "others") && (
              <Badge color='success'>
                ※ 年会費は口数 X RM 345で計算します。
              </Badge>
            )}
            <div className="mt-4">
              <p>口数合計 (Total Number of Units): <strong>{unitCalculation} 口 (units)</strong></p>
              {(stateSelection === "others") && (
                <p>年会費 = 口数 X RM 360 (Annual Membership Fee = Total Number of Units X RM 360): <strong> RM {annualFee.toLocaleString("en-US")}</strong></p>
              )}
              {(stateSelection !== "others") && (
                <p>年会費 = 口数 X RM 345 (Annual Membership Fee = Total Number of Units X RM 345): <strong> RM {annualFee.toLocaleString("en-US")}</strong></p>
              )}

              <p className="mt-3">入会金 (Admission Fee): <strong>RM {memberRegistrationFee.toLocaleString("en-US")}</strong></p>
              <p>JACTIM Foundation Fee: <strong>RM 300</strong></p>

              <p className="mt-5" style={{ fontSize: '20px' }}><strong>初年度会費 / First Year Annual Membership Fee: RM {admissionFee.toLocaleString("en-US")}</strong></p>

              <p style={{ color: '#808080' }}>※次年度以降入会金はかかりません。</p>
            </div>
          </div>
        )}

        {(membershipCategory !== "CORPORATE MEMBER/法人会員") && (
          <div className="pt-5">
            {(stateSelection === "others") && (
              <Badge color='success'>
                ※ 年会費は口数 X RM 360で計算します。
              </Badge>
            )}
            {(stateSelection !== "others") && (
              <Badge color='success'>
                ※ 年会費は口数 X RM 345で計算します。
              </Badge>
            )}
            <div className="mt-4">
              <p>口数合計 (Total Number of Units): <strong>{unitCalculation} 口 (units)</strong></p>
              {(stateSelection === "others") && (
                <p>年会費 = 口数 X RM 360 (Annual Membership Fee = Total Number of Units X RM 360): <strong> RM {annualFee.toLocaleString("en-US")}</strong></p>
              )}
              {(stateSelection !== "others") && (
                <p>年会費 = 口数 X RM 345 (Annual Membership Fee = Total Number of Units X RM 345): <strong> RM {annualFee.toLocaleString("en-US")}</strong></p>
              )}

              <p className="mt-3">入会金 (Admission Fee): <strong>RM {memberRegistrationFee}</strong></p>
              <p>JACTIM Foundation Fee: <strong>RM {foundationFee.toLocaleString("en-US")}</strong></p>

              <p className="mt-5" style={{ fontSize: '20px' }}><strong>初年度会費 / First Year Annual Membership Fee: RM {admissionFee.toLocaleString("en-US")}</strong></p>

              <p style={{ color: '#808080' }}>※次年度以降入会金はかかりません。</p>
            </div>
          </div>
        )}

      </div>

      <div className="mt-5">
        <Form className='pt-5'>
          <Label>登録代表者 / Representative Name<span style={{ color: "red" }}> (*)</span></Label>
          <FormGroup>
            <Input
              id="representativeName"
              name="representativeName"
              type="text"
              value={representativeName}
              onChange={handleChange}
            />
          </FormGroup>
        </Form>

        <FormGroup check>
          <Input 
            type="checkbox"
            onChange={handleCheckboxChange}
            checked={isChecked} 
          />
          {' '}
          <Label check>
            登録とともに申請に関してご同意いただいたものとみなします。私は、マレーシア日本人商工会議所の定款、規則、その他の内規を誠実に尊守することを誓います。
          </Label>
        </FormGroup>
      </div>

      <div className='justify-content-end pt-5 d-flex gap-3'>
        <Button 
          color="secondary" 
          outline
          onClick={handlePrevious}
          >戻る / Previous
        </Button>
        {(membershipCategory !== "CORPORATE MEMBER/法人会員") && (
          <Button 
          color="info" 
          outline
          disabled={!isChecked}
          onClick={handleSubmit}
          >
            <SpinnerInButton loading={inProgress} text="登録 / Submit" />
            {inProgress && (<span className="pl-3">登録中 / Submitting</span>)}
        </Button>
        )}
        {(membershipCategory === "CORPORATE MEMBER/法人会員") && (
          <Button 
          color="info" 
          outline
          disabled={!isChecked || !valid}
          onClick={handleSubmit}
          >
            <SpinnerInButton loading={inProgress} text="登録 / Submit" />
            {inProgress && (<span className="pl-3">登録中 / Submitting</span>)}
        </Button>
        )}
      </div>
		</div>
  )
}
